<template>
  <div class="container">
    <div class="form-sections">
      <!-- Loading state -->
      <div v-if="loading" class="empty-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
      <!-- Step Tracker Navigation -->
      <div v-if="!loading" class="tracker-container">
        <div class="tracker-steps">
          <div
            v-for="(step, index) in steps"
            :key="step.id"
            class="tracker-step"
            :class="{
              active: activeStep === step.id,
              completed: isStepCompleted(step.id),
              'last-step': index === steps.length - 1,
            }"
          >
            <div class="step-icon-container">
              <i :class="step.icon"></i>
            </div>
            <div class="step-label">{{ step.name }}</div>
          </div>
        </div>
        <div class="tracker-line"></div>
      </div>

      <!-- Owner Information -->
      <div v-if="!loading && activeStep === 'owner'" class="step-content">
        <div class="popup-columns">
          <!-- Owner Details Column -->
          <div class="popup-column details-column">
            <div class="info-section">
              <h5 class="section-title">Owner Details</h5>
              <div class="dropdown-container single-column">
                <!-- Title -->
                <div class="dropdown-item">
                  <label for="title">Title<span class="req">*</span></label>
                  <select
                    id="title"
                    v-model="title"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select Title</option>
                    <option v-for="item in titleList" :key="item" :value="item">
                      {{ item }}
                    </option>
                  </select>
                </div>

                <!-- First Name -->
                <div class="dropdown-item">
                  <label for="Title"
                    >First Name<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter First Name"
                    v-model="firstName"
                    required
                  />
                </div>

                <!-- Last Name -->
                <div class="dropdown-item">
                  <label for="LastName"
                    >Last Name<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Last Name"
                    v-model="lastName"
                    required
                  />
                </div>

                <!-- Email Address -->
                <div class="dropdown-item">
                  <label for="email">Email Address</label>
                  <input
                    type="email"
                    placeholder="Enter Email Address"
                    v-model="email"
                  />
                </div>

                <!-- Mobile Number -->
                <div class="dropdown-item">
                  <label for="mobile"
                    >Mobile Number<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Mobile Number"
                    v-model="mobile"
                    maxlength="10"
                    required
                  />
                </div>

                <!-- Date of Birth -->
                <div class="dropdown-item">
                  <label for="dob"
                    >Date of Birth<span class="req">*</span></label
                  >
                  <input
                    type="date"
                    placeholder="Enter DOB"
                    v-model="dob"
                    required
                  />
                </div>

                <!-- Identity Type -->
                <div class="dropdown-item">
                  <label for="identityType"
                    >Identity Type<span class="req">*</span></label
                  >
                  <select
                    id="identityType"
                    v-model="identityType"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select Identity Type</option>
                    <option
                      v-for="type in identityTypes"
                      :key="type"
                      :value="type"
                    >
                      {{ type }}
                    </option>
                  </select>
                </div>

                <!-- Identity Number -->
                <div class="dropdown-item">
                  <label for="identityNumber"
                    >Identity Number<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Identity Number"
                    v-model="identityNumber"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- ID Documents Column -->
          <div class="popup-column documents-column">
            <div class="info-section">
              <h5 class="section-title">ID Documents</h5>
              <div class="documents-grid">
                <!-- ID Front Image -->
                <div class="document-item">
                  <div
                    class="document-image-container"
                    @mouseenter="showDocumentActions = 'idFront'"
                    @mouseleave="showDocumentActions = null"
                    @click="triggerFileInput('idFrontImage')"
                  >
                    <!-- Show image if it's an image type -->
                    <img
                      v-if="
                        idFrontImagePreview && isImageDocument(idFrontImageType)
                      "
                      :src="idFrontImagePreview"
                      alt="ID Front"
                      class="document-image"
                    />

                    <!-- Show PDF thumbnail if it's PDF -->
                    <div
                      v-else-if="
                        idFrontImagePreview && isPdfDocument(idFrontImageType)
                      "
                      class="pdf-preview"
                    >
                      <i class="fas fa-file-pdf"></i>
                      <span>ID Front Document</span>
                      <small>PDF Document</small>
                    </div>

                    <!-- Show invalid thumbnail if document is invalid -->
                    <div
                      v-else-if="idFrontImagePreview"
                      class="invalid-preview"
                    >
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>Invalid Document</span>
                      <small>Unsupported format</small>
                    </div>

                    <!-- Show placeholder if no document -->
                    <div v-else class="document-placeholder">
                      <i class="fas fa-id-card"></i>
                      <span>Click to Upload ID Front</span>
                      <small>Supported: PDF, JPG, PNG, JPEG</small>
                    </div>

                    <!-- Document Actions Overlay - Show only for valid documents that haven't been changed -->
                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'idFront' &&
                        idFrontImagePreview &&
                        isValidDocument(
                          idFrontImagePreview,
                          idFrontImageType,
                        ) &&
                        !isIdFrontChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              idFrontImagePreview,
                              'ID Front Image',
                              idFrontImageType,
                              false,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('idFrontImage')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('idFront')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <!-- Document Actions Overlay - Show for changed documents -->
                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'idFront' &&
                        idFrontImagePreview &&
                        isValidDocument(
                          idFrontImagePreview,
                          idFrontImageType,
                        ) &&
                        isIdFrontChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              idFrontImagePreview,
                              'ID Front Image',
                              idFrontImageType,
                              true,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('idFrontImage')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('idFront')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <!-- Upload Progress -->
                    <div v-if="uploading.idFront" class="upload-progress">
                      <div class="progress-bar">
                        <div
                          class="progress-fill"
                          :style="{ width: uploadProgress.idFront + '%' }"
                        ></div>
                      </div>
                      <span>{{ uploadProgress.idFront }}%</span>
                    </div>
                  </div>
                  <div class="document-label">ID Front</div>
                  <input
                    type="file"
                    id="idFrontImage"
                    @change="handleIdFrontImage"
                    accept=".pdf,image/jpeg,image/jpg,image/png"
                    class="file-input-hidden"
                    ref="idFrontImage"
                  />
                </div>

                <!-- ID Back Image -->
                <div
                  v-if="
                    identityType !== 'Passport' &&
                    identityType !== 'Driving License'
                  "
                  class="document-item"
                >
                  <div
                    class="document-image-container"
                    @mouseenter="showDocumentActions = 'idBack'"
                    @mouseleave="showDocumentActions = null"
                    @click="triggerFileInput('idBackImage')"
                  >
                    <img
                      v-if="
                        idBackImagePreview && isImageDocument(idBackImageType)
                      "
                      :src="idBackImagePreview"
                      alt="ID Back"
                      class="document-image"
                    />
                    <div
                      v-else-if="
                        idBackImagePreview && isPdfDocument(idBackImageType)
                      "
                      class="pdf-preview"
                    >
                      <i class="fas fa-file-pdf"></i>
                      <span>ID Back Document</span>
                      <small>PDF Document</small>
                    </div>
                    <div v-else-if="idBackImagePreview" class="invalid-preview">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>Invalid Document</span>
                      <small>Unsupported format</small>
                    </div>
                    <div v-else class="document-placeholder">
                      <i class="fas fa-id-card"></i>
                      <span>Click to Upload ID Back</span>
                      <small>Supported: PDF, JPG, PNG, JPEG</small>
                    </div>

                    <!-- Document Actions Overlay -->
                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'idBack' &&
                        idBackImagePreview &&
                        isValidDocument(idBackImagePreview, idBackImageType) &&
                        !isIdBackChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              idBackImagePreview,
                              'ID Back Image',
                              idBackImageType,
                              false,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('idBackImage')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('idBack')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'idBack' &&
                        idBackImagePreview &&
                        isValidDocument(idBackImagePreview, idBackImageType) &&
                        isIdBackChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              idBackImagePreview,
                              'ID Back Image',
                              idBackImageType,
                              true,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('idBackImage')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('idBack')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <!-- Upload Progress -->
                    <div v-if="uploading.idBack" class="upload-progress">
                      <div class="progress-bar">
                        <div
                          class="progress-fill"
                          :style="{ width: uploadProgress.idBack + '%' }"
                        ></div>
                      </div>
                      <span>{{ uploadProgress.idBack }}%</span>
                    </div>
                  </div>
                  <div class="document-label">ID Back</div>
                  <input
                    type="file"
                    id="idBackImage"
                    @change="handleIdBackImage"
                    accept=".pdf,image/jpeg,image/jpg,image/png"
                    class="file-input-hidden"
                    ref="idBackImage"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step Navigation - Only Next button for first step -->
        <div class="step-navigation">
          <div class="step-navigation-left">
            <!-- Empty on first step -->
          </div>
          <div class="step-navigation-right">
            <button type="button" class="btn btn-primary" @click="goToNextStep">
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- Business Information -->
      <div v-if="!loading && activeStep === 'business'" class="step-content">
        <div class="popup-columns">
          <!-- Business Details Column -->
          <div class="popup-column details-column">
            <div class="info-section">
              <h5 class="section-title">Business Details</h5>
              <div class="dropdown-container single-column">
                <!-- Business Name -->
                <div class="dropdown-item">
                  <label for="businessName"
                    >Business Name<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Business Name"
                    v-model="businessName"
                    required
                  />
                </div>

                <!-- Company Selection - Add :disabled attribute -->
                <div class="dropdown-item">
                  <label for="companyId"
                    >Company<span class="req">*</span></label
                  >
                  <select
                    id="companyId"
                    v-model="selectedCompany"
                    class="scrollable-dropdown"
                    :disabled="isCompanyAutoFilled"
                    required
                  >
                    <option disabled value="">Select Company</option>
                    <option
                      v-for="company in companyList"
                      :key="company.id"
                      :value="company.id"
                    >
                      {{ company.company_name }}
                    </option>
                  </select>
                </div>

                <!-- Pricing Category -->
                <div class="dropdown-item">
                  <label for="pricingCategory"
                    >Pricing Category<span class="req">*</span></label
                  >
                  <select
                    id="pricingCategory"
                    v-model="selectedPricingCategory"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select Pricing Category</option>
                    <option
                      v-for="dealer in pricingCategoryList"
                      :key="dealer.id"
                      :value="dealer.id"
                    >
                      {{ dealer.dealer_cat_name }}
                    </option>
                  </select>
                </div>

                <!-- Business Type -->
                <div class="dropdown-item">
                  <label for="businessType"
                    >Business Type<span class="req">*</span></label
                  >
                  <select
                    id="businessType"
                    v-model="selectedBusinessType"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select Business Type</option>
                    <option
                      v-for="type in businessTypesList"
                      :key="type.id"
                      :value="type.id"
                    >
                      {{ type.name }}
                    </option>
                  </select>
                </div>

                <!-- Nature of Business -->
                <div class="dropdown-item">
                  <label for="natureOfBusiness"
                    >Nature of Business<span class="req">*</span></label
                  >
                  <select
                    id="natureOfBusiness"
                    v-model="selectedNatureOfBusiness"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select Nature of Business</option>
                    <option
                      v-for="nature in natureOfBusinessList"
                      :key="nature.id"
                      :value="nature.id"
                    >
                      {{ nature.businesses_name }}
                    </option>
                  </select>
                </div>

                <!-- Route -->
                <div class="dropdown-item">
                  <label for="dealerRoute"
                    >Route<span class="req">*</span></label
                  >
                  <select
                    id="dealerRoute"
                    v-model="selectedRoute"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select Route</option>
                    <option
                      v-for="route in routesList"
                      :key="route.id"
                      :value="route.id"
                    >
                      {{ route.route_name }}
                    </option>
                  </select>
                </div>

                <!-- Business Registration Number -->
                <div class="dropdown-item">
                  <label for="businessRegNumber"
                    >Business Registration Number<span class="req"
                      >*</span
                    ></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Business Registration Number"
                    v-model="businessRegNumber"
                    required
                  />
                </div>

                <!-- Address -->
                <div class="dropdown-item">
                  <label for="address1"
                    >Address<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Address"
                    v-model="address"
                    required
                  />
                </div>

                <!-- City -->
                <div class="dropdown-item">
                  <label for="city">City<span class="req">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter City"
                    v-model="city"
                    required
                  />
                </div>

                <!-- District -->
                <div class="dropdown-item">
                  <label for="district"
                    >District<span class="req">*</span></label
                  >
                  <select
                    id="district"
                    v-model="selectedDistrict"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select District</option>
                    <option
                      v-for="district in districtList"
                      :key="district.id"
                      :value="district.id"
                    >
                      {{ district.name }}
                    </option>
                  </select>
                </div>

                <!-- Postal Code -->
                <div class="dropdown-item">
                  <label for="postCode"
                    >Postal Code<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Postal Code"
                    v-model="postalCode"
                    required
                  />
                </div>

                <!-- Distributor -->
                <div class="dropdown-item">
                  <label for="distributor"
                    >Distributor<span class="req">*</span></label
                  >
                  <select
                    id="distributor"
                    v-model="selectedDistributor"
                    class="scrollable-dropdown"
                    :disabled="isDistributorAutoFilled"
                    required
                  >
                    <option disabled value="">Select Distributor</option>
                    <option
                      v-for="distributor in distributorList"
                      :key="distributor.profile_id"
                      :value="distributor.profile_id"
                    >
                      {{ distributor.name }}
                    </option>
                  </select>
                </div>

                <!-- Dealer Group -->
                <div class="dropdown-item">
                  <label for="dealerGroup"
                    >Dealer Group</label
                  >
                  <select
                    id="dealerGroup"
                    v-model="selectedDealerGroup"
                    class="scrollable-dropdown"
                  >
                    <option value="">No Dealer Group</option>
                    <option
                      v-for="group in dealerGroupList"
                      :key="group.group_id"
                      :value="group.group_id"
                    >
                      {{ group.group_name }}
                    </option>
                  </select>
                </div>

                <!-- Sales Executive -->
                <div class="dropdown-item">
                  <label for="salesExecutive"
                    >Sales Executive<span class="req">*</span></label
                  >
                  <select
                    id="salesExecutive"
                    v-model="selectedSalesExecutive"
                    class="scrollable-dropdown"
                    required
                  >
                    <option disabled value="">Select Sales Executive</option>
                    <option
                      v-for="exec in salesExecutiveList"
                      :key="exec.profile_id"
                      :value="exec.profile_id"
                    >
                      {{ exec.name }}
                    </option>
                  </select>
                </div>

                <!-- Latitude -->
                <div class="dropdown-item">
                  <label for="latitude"
                    >Latitude<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Latitude"
                    v-model="latitude"
                    required
                  />
                </div>

                <!-- Longitude -->
                <div class="dropdown-item">
                  <label for="longitude"
                    >Longitude<span class="req">*</span></label
                  >
                  <input
                    type="text"
                    placeholder="Enter Longitude"
                    v-model="longitude"
                    required
                  />
                </div>

                <!-- SAP Code -->
                <div class="dropdown-item">
                  <label for="sapCode">SAP Code</label>
                  <input
                    type="text"
                    placeholder="Enter SAP Code"
                    v-model="sapCode"
                  />
                </div>

                <!-- Home Delivery Checkbox -->
                <div class="homeDelivery-checkbox-container">
                  <input
                    type="checkbox"
                    class="checkbox"
                    id="is_home_delivery"
                    v-model="is_home_delivery"
                    :true-value="1"
                    :false-value="0"
                  />
                  <label for="is_home_delivery" class="homeDelivery-label"
                    >Home Delivery Available</label
                  >
                </div>

                <!-- Eligible for Trust Checkbox -->
                <div class="homeDelivery-checkbox-container">
                  <input
                    type="checkbox"
                    class="checkbox"
                    id="is_eligible_trust"
                    v-model="is_eligible_trust"
                    :true-value="1"
                    :false-value="0"
                  />
                  <label for="is_eligible_trust" class="homeDelivery-label"
                    >Eligible for Trust</label
                  >
                </div>

                <!-- Is Active -->
                <div class="is-active-container">
                  <div class="is-active-label">Is Active?</div>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="isActive"
                      :true-value="1"
                      :false-value="0"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Business Documents Column -->
          <div class="popup-column documents-column">
            <div class="info-section">
              <h5 class="section-title">Business Documents</h5>
              <div class="documents-grid">
                <!-- Business Registration Document -->
                <div class="document-item">
                  <div
                    class="document-image-container"
                    @mouseenter="showDocumentActions = 'brDocument'"
                    @mouseleave="showDocumentActions = null"
                    @click="triggerFileInput('brDocument')"
                  >
                    <!-- Show image if it's an image type -->
                    <img
                      v-if="
                        brDocumentPreview && isImageDocument(brDocumentType)
                      "
                      :src="brDocumentPreview"
                      alt="Business Registration"
                      class="document-image"
                    />

                    <!-- Show PDF thumbnail if it's PDF -->
                    <div
                      v-else-if="
                        brDocumentPreview && isPdfDocument(brDocumentType)
                      "
                      class="pdf-preview"
                    >
                      <i class="fas fa-file-pdf"></i>
                      <span>Business Registration Document</span>
                      <small>PDF Document</small>
                    </div>

                    <!-- Show invalid thumbnail -->
                    <div v-else-if="brDocumentPreview" class="invalid-preview">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>Invalid Document</span>
                      <small>Unsupported format</small>
                    </div>

                    <div v-else class="document-placeholder">
                      <i class="fas fa-file-contract"></i>
                      <span>Click to Upload Business Registration</span>
                      <small>Supported: PDF, JPG, PNG, JPEG</small>
                    </div>

                    <!-- Document Actions Overlay -->
                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'brDocument' &&
                        brDocumentPreview &&
                        isValidDocument(brDocumentPreview, brDocumentType) &&
                        !isBrDocumentChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              brDocumentPreview,
                              'Business Registration Document',
                              brDocumentType,
                              false,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('brDocument')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('brDocument')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'brDocument' &&
                        brDocumentPreview &&
                        isValidDocument(brDocumentPreview, brDocumentType) &&
                        isBrDocumentChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              brDocumentPreview,
                              'Business Registration Document',
                              brDocumentType,
                              true,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('brDocument')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('brDocument')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <!-- Upload Progress -->
                    <div v-if="uploading.brDocument" class="upload-progress">
                      <div class="progress-bar">
                        <div
                          class="progress-fill"
                          :style="{ width: uploadProgress.brDocument + '%' }"
                        ></div>
                      </div>
                      <span>{{ uploadProgress.brDocument }}%</span>
                    </div>
                  </div>
                  <div class="document-label">Business Registration</div>
                  <input
                    type="file"
                    id="brDocument"
                    @change="handleBrDocument"
                    accept=".pdf,image/jpeg,image/jpg,image/png"
                    class="file-input-hidden"
                    ref="brDocument"
                  />
                </div>

                <!-- Agreement Document -->
                <div class="document-item">
                  <div
                    class="document-image-container"
                    @mouseenter="showDocumentActions = 'agreementDocument'"
                    @mouseleave="showDocumentActions = null"
                    @click="triggerFileInput('agreementDocument')"
                  >
                    <img
                      v-if="
                        agreementDocumentPreview &&
                        isImageDocument(agreementDocumentType)
                      "
                      :src="agreementDocumentPreview"
                      alt="Agreement"
                      class="document-image"
                    />

                    <div
                      v-else-if="
                        agreementDocumentPreview &&
                        isPdfDocument(agreementDocumentType)
                      "
                      class="pdf-preview"
                    >
                      <i class="fas fa-file-pdf"></i>
                      <span>Agreement Document</span>
                      <small>PDF Document</small>
                    </div>

                    <div
                      v-else-if="agreementDocumentPreview"
                      class="invalid-preview"
                    >
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>Invalid Document</span>
                      <small>Unsupported format</small>
                    </div>

                    <div v-else class="document-placeholder">
                      <i class="fas fa-handshake"></i>
                      <span>Click to Upload Agreement</span>
                      <small>Supported: PDF, JPG, PNG, JPEG</small>
                    </div>

                    <!-- Document Actions Overlay -->
                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'agreementDocument' &&
                        agreementDocumentPreview &&
                        isValidDocument(
                          agreementDocumentPreview,
                          agreementDocumentType,
                        ) &&
                        !isAgreementDocumentChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              agreementDocumentPreview,
                              'Agreement Document',
                              agreementDocumentType,
                              false,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('agreementDocument')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('agreementDocument')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <div
                      class="document-actions-overlay"
                      v-if="
                        showDocumentActions === 'agreementDocument' &&
                        agreementDocumentPreview &&
                        isValidDocument(
                          agreementDocumentPreview,
                          agreementDocumentType,
                        ) &&
                        isAgreementDocumentChanged
                      "
                    >
                      <div class="action-buttons">
                        <button
                          class="action-btn preview-btn"
                          @click.stop="
                            handleDocumentClick(
                              agreementDocumentPreview,
                              'Agreement Document',
                              agreementDocumentType,
                              true,
                            )
                          "
                          title="Preview"
                        >
                          <i class="fas fa-search-plus"></i>
                        </button>
                        <button
                          class="action-btn change-btn"
                          @click.stop="triggerFileInput('agreementDocument')"
                          title="Change"
                        >
                          <i class="fas fa-sync"></i>
                        </button>
                        <button
                          class="action-btn remove-btn"
                          @click.stop="removeDocument('agreementDocument')"
                          title="Remove"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <div class="action-labels">
                        <span>Preview</span>
                        <span>Change</span>
                        <span>Remove</span>
                      </div>
                    </div>

                    <!-- Upload Progress -->
                    <div
                      v-if="uploading.agreementDocument"
                      class="upload-progress"
                    >
                      <div class="progress-bar">
                        <div
                          class="progress-fill"
                          :style="{
                            width: uploadProgress.agreementDocument + '%',
                          }"
                        ></div>
                      </div>
                      <span>{{ uploadProgress.agreementDocument }}%</span>
                    </div>
                  </div>
                  <div class="document-label">Agreement</div>
                  <input
                    type="file"
                    id="agreementDocument"
                    @change="handleAgreementDocument"
                    accept=".pdf,image/jpeg,image/jpg,image/png"
                    class="file-input-hidden"
                    ref="agreementDocument"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step Navigation - Both Back and Next buttons -->
        <div class="step-navigation">
          <div class="step-navigation-left">
            <button
              type="button"
              class="btn btn-secondary"
              @click="goToPreviousStep"
            >
              Back
            </button>
          </div>
          <div class="step-navigation-right">
            <button type="button" class="btn btn-primary" @click="goToNextStep">
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- Financial Information -->
      <div v-if="!loading && activeStep === 'finance'" class="step-content">
        <div class="info-section full-width-section">
          <h5 class="section-title">Financial Information</h5>

          <div class="finance-container">
            <!-- VAT / SVAT row -->
            <div class="finance-row">
              <!-- VAT Section -->
              <div class="finance-item vat-section">
                <div class="vat-checkbox-container">
                  <input
                    type="checkbox"
                    class="checkbox"
                    id="vat"
                    v-model="isCheckedVat"
                    :true-value="1"
                    :false-value="0"
                  />
                  <label for="vat" class="vat-label">VAT No</label>
                </div>

                <div v-if="isCheckedVat" class="vat-input-container">
                  <input
                    type="text"
                    id="vat"
                    placeholder="Enter VAT Number"
                    v-model="vatNo"
                    class="vat-input"
                  />

                  <!-- New VAT parent details -->
                  <div class="vat-extra-grid">
                    <div class="vat-extra-item">
                      <label for="vatName">VAT Name</label>
                      <input
                        type="text"
                        id="vatName"
                        placeholder="Enter VAT Name"
                        v-model="vatName"
                        class="vat-input"
                      />
                    </div>

                    <div class="vat-extra-item">
                      <label for="vatContactNo">VAT Contact No</label>
                      <input
                        type="text"
                        id="vatContactNo"
                        placeholder="Enter VAT Contact No"
                        v-model="vatContactNo"
                        maxlength="10"
                        class="vat-input"
                      />
                    </div>

                    <div class="vat-extra-item vat-extra-full">
                      <label for="vatAddress">VAT Address</label>
                      <input
                        type="text"
                        id="vatAddress"
                        placeholder="Enter VAT Address"
                        v-model="vatAddress"
                        class="vat-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- SVAT Section -->
              <div class="finance-item svat-section">
                <div class="svat-checkbox-container">
                  <input
                    type="checkbox"
                    class="checkbox"
                    id="svat"
                    v-model="isCheckedSvat"
                    :true-value="1"
                    :false-value="0"
                  />
                  <label for="svat" class="svat-label">SVAT No</label>
                </div>

                <div v-if="isCheckedSvat" class="svat-input-container">
                  <input
                    type="text"
                    id="svat"
                    placeholder="Enter SVAT Number"
                    v-model="svatNo"
                    class="svat-input"
                  />
                </div>
              </div>
            </div>

            <!-- Credit Limit row -->
            <div class="finance-row">
              <!-- Credit Limit Value -->
              <div class="finance-item">
                <label for="creditLimit">
                  Credit Limit Value<span class="req">*</span>
                </label>
                <input
                  type="number"
                  id="creditLimit"
                  placeholder="Enter Credit Limit"
                  v-model="creditLimit"
                  required
                />
              </div>

              <!-- Credit Limit Days -->
              <div class="finance-item">
                <label for="creditLimitDays">
                  Credit Limit Days<span class="req">*</span>
                </label>
                <input
                  type="number"
                  id="creditLimitDays"
                  placeholder="Enter No of days"
                  v-model="creditLimitDays"
                  required
                />
              </div>
            </div>
          </div>

          <!-- Eligible Printed Invoice -->
          <div class="eligible-printed-invoice-container">
            <div class="eligible-invoice-label">
              Is eligible for printed invoice?
            </div>

            <label class="toggle-switch">
              <input
                type="checkbox"
                v-model="eligiblePrintedInvoice"
                :true-value="1"
                :false-value="0"
              />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

        <!-- Step Navigation - Both Back and Next buttons -->
        <div class="step-navigation">
          <div class="step-navigation-left">
            <button
              type="button"
              class="btn btn-secondary"
              @click="goToPreviousStep"
            >
              Back
            </button>
          </div>

          <div class="step-navigation-right">
            <button type="button" class="btn btn-primary" @click="goToNextStep">
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- Stock Management -->
      <div v-if="!loading && activeStep === 'stock'" class="step-content">
        <div class="info-section">
          <div class="stock-table-container">
            <table class="table-container">
              <thead>
                <tr>
                  <th class="item-image-col">Item Image</th>
                  <th class="item-name-col">Item Name</th>
                  <th class="reorder-col">
                    <div class="reorder-header">
                      <span>Re-Order Levels</span>
                      <div class="min-max-labels">
                        <span>Min</span>
                        <span>Max</span>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in stockItems" :key="index">
                  <td class="item-image-cell">
                    <div class="item-image-container">
                      <img
                        v-if="
                          item.itemImage && isValidBase64Image(item.itemImage)
                        "
                        :src="getBase64ImageSrc(item.itemImage)"
                        :alt="item.itemName"
                        class="item-image"
                        @error="handleStockImageError(index)"
                      />
                      <div v-else class="item-placeholder">
                        <i class="fa-solid fa-image"></i>
                      </div>
                      <div
                        class="image-overlay"
                        v-if="
                          item.itemImage && isValidBase64Image(item.itemImage)
                        "
                      >
                        <i class="fas fa-search-plus"></i>
                      </div>
                    </div>
                  </td>
                  <td class="item-name-cell">{{ item.itemName || "N/A" }}</td>
                  <td class="reorder-cell">
                    <div class="reorder-levels">
                      <div class="min-level">
                        <input
                          type="number"
                          v-model="item.minQty"
                          placeholder="Min"
                          class="qty-input"
                        />
                      </div>
                      <div class="max-level">
                        <input
                          type="number"
                          v-model="item.maxQty"
                          placeholder="Max"
                          class="qty-input"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Empty state -->
            <div v-if="stockLoading" class="empty-state">
              <i class="fas fa-spinner fa-spin"></i>
              <p>Loading stock items...</p>
            </div>

            <div v-else-if="stockItems.length === 0" class="empty-state">
              <i class="fas fa-box-open"></i>
              <p>No stock items found</p>
            </div>
          </div>
        </div>

        <!-- Step Navigation - Both Back and Next buttons -->
        <div class="step-navigation">
          <div class="step-navigation-left">
            <button
              type="button"
              class="btn btn-secondary"
              @click="goToPreviousStep"
            >
              Back
            </button>
          </div>
          <div class="step-navigation-right">
            <button
              type="button"
              class="btn btn-success"
              @click="submitDealerUpdate"
              :disabled="updating || stockLoading"
            >
              <span
                v-if="updating"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span v-if="!updating">Update</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Updated Document View Modal -->
    <DocumentViewModal
      :show="showImageModal"
      :documentSrc="selectedImage"
      :documentTitle="selectedImageTitle"
      :documentType="selectedDocumentType"
      @close="closeImageModal"
    />
  </div>
</template>

<script>
import {
  viewDealerDetails,
  fetchCompanyListForUserForm,
  fetchDistrictListForUserForm,
  fetchDealerCategoryList,
  fetchDistributorsList,
  fetchBusinessTypesList,
  fetchNatureOfBusinessList,
  fetchRouteList,
  fetchItemNameList,
  getDealerGroups,
  fetchSalesExecutives,
  updateDealer,
} from "@/services/dataService";
import { useToast } from "vue-toastification";
import DocumentViewModal from "@/pages/Modal/documents/documentViewModal.vue";

export default {
  components: { DocumentViewModal },
  data() {
    return {
      id: this.$route.params.id,
      token: localStorage.getItem("token_set") || "",
      profileId: localStorage.getItem("profile_id") || "",
      userCategory: localStorage.getItem("user_label") || "",
      companyId: localStorage.getItem("company_id") || "",
      reportingProfileId: localStorage.getItem("reportingL1ProfileId"),
      loading: false,
      updating: false,

      stockLoading: false,
      stockItemsLoaded: false,
      dealerDetails: null,

      // Step Management
      activeStep: "owner",
      steps: [
        { id: "owner", name: "Owner Information", icon: "fas fa-user" },
        {
          id: "business",
          name: "Business Information",
          icon: "fas fa-building",
        },
        {
          id: "finance",
          name: "Financial Information",
          icon: "fa-solid fa-coins",
        },
        {
          id: "stock",
          name: "Stock Management",
          icon: "fas fa-boxes",
        },
      ],

      // Lists
      titleList: ["Mr", "Mrs", "Miss", "Ms"],
      identityTypes: ["NIC", "Passport", "Driving License"],
      companyList: [],
      districtList: [],
      pricingCategoryList: [],
      businessTypesList: [],
      natureOfBusinessList: [],
      distributorList: [],
      routesList: [],
      dealerGroupList: [],
      salesExecutiveList: [],

      // Owner Information
      title: "",
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      dob: "",
      identityType: "",
      identityNumber: "",
      idFrontImage: null,
      idFrontImagePreview: null,
      idBackImage: null,
      idBackImagePreview: null,

      // Document types from API
      idFrontImageType: null,
      idBackImageType: null,
      brDocumentType: null,
      agreementDocumentType: null,

      // Store original document data
      originalIdFrontImage: null,
      originalIdBackImage: null,
      originalBrDocument: null,
      originalAgreementDocument: null,
      originalIdFrontImageType: null,
      originalIdBackImageType: null,
      originalBrDocumentType: null,
      originalAgreementDocumentType: null,

      // Business Information
      businessName: "",
      selectedCompany: "",
      selectedPricingCategory: "",
      selectedBusinessType: "",
      selectedNatureOfBusiness: "",
      selectedRoute: "",
      businessRegNumber: "",
      address: "",
      city: "",
      selectedDistrict: "",
      postalCode: "",
      selectedDistributor: "",
      selectedDealerGroup: "",
      selectedSalesExecutive: "",
      latitude: "",
      longitude: "",
      brDocument: null,
      brDocumentPreview: null,
      brDocumentName: "",
      agreementDocument: null,
      agreementDocumentPreview: null,
      agreementDocumentName: "",
      sapCode: "",
      is_home_delivery: 0,
      is_eligible_trust: 0,
      isActive: 1,

      // Finance Information
      isCheckedVat: 0,
      isCheckedSvat: 0,
      svatNo: "",
      vatNo: "",
      vatAddress: "",
      vatName: "",
      vatContactNo: "",
      creditLimitDays: "",
      creditLimit: "",
      eligiblePrintedInvoice: 1,

      // Stock Management
      itemsList: [],
      stockItems: [],
      stockImageErrors: {},

      // Dealer Visit Frequency - simplified approach
      dealerVisitFrequency: {
        monday: "0",
        tuesday: "0",
        wednesday: "0",
        thursday: "0",
        friday: "0",
        saturday: "0",
        sunday: "0",
      },

      // Image Modal
      showImageModal: false,
      selectedImage: "",
      selectedImageTitle: "",
      selectedDocumentType: null,

      // Document Actions
      showDocumentActions: null,

      // Upload States
      uploading: {
        idFront: false,
        idBack: false,
        brDocument: false,
        agreementDocument: false,
      },
      uploadProgress: {
        idFront: 0,
        idBack: 0,
        brDocument: 0,
        agreementDocument: 0,
      },

      // Data fields freeze status management
      isCompanyAutoFilled: true, // Company change is disabled
      isDistributorAutoFilled: false, // Will be set based on user category

      // Document change tracking
      isIdFrontChanged: false,
      isIdBackChanged: false,
      isBrDocumentChanged: false,
      isAgreementDocumentChanged: false,
    };
  },

  created() {
    this.loadDealerDetails();
    this.loadCompanyList();
    this.loadDistrictList();
    this.loadPricingCategoryList();
    this.loadBusinessTypes();
    this.loadRoutes();
    this.loadDistributors();
    this.loadDistributorId();
    this.loadDealerGroups();
    this.loadSalesExecutives();
  },

  setup() {
    const toast = useToast();

    return {
      toast,
    };
  },

  watch: {
    isCheckedVat(newVal) {
      if (newVal === 0) {
        this.vatNo = "";
      }
    },
    isCheckedSvat(newVal) {
      if (newVal === 0) {
        this.svatNo = "";
      }
    },
    selectedBusinessType(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.loadBusinessNatures();
        this.selectedNatureOfBusiness = ""; // reset nature dropdown when business type changes
      }
    },
    itemsList() {
      this.tryBuildStockItems();
    },

    dealerDetails(newVal) {
      if (newVal && this.itemsList.length > 0) {
        this.processDealerStock(newVal.dealer_stock);
      }
    },

  },

  methods: {
    // Load dealer groups
    async loadDealerGroups() {
      try {
        const response = await getDealerGroups(this.profileId, this.token);
        this.dealerGroupList = response.data.data || [];
        console.log("Fetched dealer groups:", this.dealerGroupList);
      } catch (error) {
        console.error("Error fetching dealer groups:", error);
      }
    },

    // Load sales executives for the selected distributor
    async loadSalesExecutives() {
      let idToUse = this.profileId;
      if (this.userCategory === "DISTRIBUTOR BACK OFFICE") {
        idToUse = this.reportingProfileId;
      }
      try {
        const response = await fetchSalesExecutives(
          this.profileId,
          idToUse,
          this.token,
        );
        this.salesExecutiveList = response.data.data || [];
        console.log("Fetched sales executives:", this.salesExecutiveList);
      } catch (error) {
        console.error("Error fetching sales executives:", error);
        this.salesExecutiveList = [];
      }
    },

    // Load dealer details
    async loadDealerDetails() {
      try {
        this.loading = true;

        const response = await viewDealerDetails(this.id, this.token);
        const dealer = response.data.data;

        console.log("dealer details", dealer);
        if (dealer) {
          this.dealerDetails = dealer;

          // Store original document URLs and types
          this.originalIdFrontImage = dealer.id_front_image;
          this.originalIdBackImage = dealer.id_back_image;
          this.originalBrDocument = dealer.br_document;
          this.originalAgreementDocument = dealer.agreement_document;

          this.originalIdFrontImageType = dealer.id_front_mime;
          this.originalIdBackImageType = dealer.id_back_mime;
          this.originalBrDocumentType = dealer.br_mime;
          this.originalAgreementDocumentType = dealer.agreement_mime;

          // Set current document types
          this.idFrontImageType = dealer.id_front_mime;
          this.idBackImageType = dealer.id_back_mime;
          this.brDocumentType = dealer.br_mime;
          this.agreementDocumentType = dealer.agreement_mime;

          // Owner Information
          this.title = dealer.title;
          this.firstName = dealer.first_name;
          this.lastName = dealer.last_name;
          this.email = dealer.email;
          this.mobile = dealer.mobile;
          this.dob = dealer.dob;
          this.identityType = dealer.identity_type;
          this.identityNumber = dealer.identity_number;
          this.idFrontImagePreview = dealer.id_front_image;
          this.idBackImagePreview = dealer.id_back_image;

          // Business Information
          this.businessName = dealer.business_name;
          this.selectedCompany = dealer.company_id;
          this.selectedPricingCategory = dealer.dealer_cat_id;
          this.selectedRoute = dealer.route_id;
          this.businessRegNumber = dealer.business_reg_number;
          this.address = dealer.address1;
          this.city = dealer.city;
          this.selectedDistrict = dealer.district_id;
          this.postalCode = dealer.post_code;
          this.selectedDistributor = dealer.distributor_id;
          this.selectedDealerGroup = dealer.dealer_group_id && Number(dealer.dealer_group_id) !== 0 ? dealer.dealer_group_id : "";
          this.sapCode = dealer.sap_code;
          this.latitude = dealer.latitude;
          this.longitude = dealer.longitude;
          this.brDocumentPreview = dealer.br_document;
          this.agreementDocumentPreview = dealer.agreement_document;
          this.is_home_delivery = dealer.is_home_delivery;
          this.is_eligible_trust = dealer.is_eligible_trust;
          this.isActive = dealer.is_active;

          // Finance Information
          this.isCheckedVat = (dealer.vat_liable == "1" || dealer.vat_liable === 1) ? 1 : 0;
          this.vatNo = dealer.vat_no;
          this.vatAddress = dealer.parent_address || "";
          this.vatName = dealer.parent_name || "";
          this.vatContactNo = dealer.parent_contact_no || "";
          this.isCheckedSvat = (dealer.svat_liable == "1" || dealer.svat_liable === 1) ? 1 : 0;
          this.svatNo = dealer.svat_no;
          this.creditLimitDays = dealer.credit_limit_days;
          this.creditLimit = dealer.credit_limit;
          this.eligiblePrintedInvoice = dealer.is_print;

          // Set Business Type FIRST, then load natures and set Nature of Business
          this.selectedBusinessType = dealer.dealer_type_id;

          // Independent loads: one failing must not block the other
          try {
            await this.loadBusinessNatures();
            this.selectedNatureOfBusiness = dealer.nature_of_businesses_id;
          } catch (e) {
            console.error("Nature of business load failed:", e);
          }

          try {
            await this.loadSalesExecutives();
            this.selectedSalesExecutive = dealer.sales_executive_id;
          } catch (e) {
            console.error("Sales executive load failed:", e);
          }

          // Stock Management - Wait for itemsList to be loaded first
          if (this.itemsList.length > 0) {
            this.processDealerStock(dealer.dealer_stock);
          }

          // Dealer Visit Frequency
          if (dealer.visit_frequency) {
            this.dealerVisitFrequency = {
              monday: dealer.visit_frequency.monday || "0",
              tuesday: dealer.visit_frequency.tuesday || "0",
              wednesday: dealer.visit_frequency.wednesday || "0",
              thursday: dealer.visit_frequency.thursday || "0",
              friday: dealer.visit_frequency.friday || "0",
              saturday: dealer.visit_frequency.saturday || "0",
              sunday: dealer.visit_frequency.sunday || "0",
            };
          }
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        this.loading = false;
      }
    },

    // Helper methods for document type checking
    isImageDocument(documentType) {
      return documentType && documentType.startsWith("image/");
    },
    isPdfDocument(documentType) {
      return documentType === "application/pdf";
    },
    isValidDocument(documentUrl, documentType) {
      if (!documentUrl || !documentType) return false;
      return (
        this.isImageDocument(documentType) || this.isPdfDocument(documentType)
      );
    },

    // Load fields automatically - Distributor
    async loadDistributorId() {
      this.isDistributorAutoFilled =
        this.userCategory === "DISTRIBUTOR" ||
        this.userCategory === "DISTRIBUTOR BACK OFFICE";
    },

    // Load company list
    async loadCompanyList() {
      try {
        this.loading = true;
        const response = await fetchCompanyListForUserForm(this.token);
        this.companyList = response.data.data || [];
        console.log("Fetched company list:", this.companyList);
      } catch (error) {
        console.error("Error fetching company list:", error);
      } finally {
        this.loading = false;
      }
    },

    // Load district list
    async loadDistrictList() {
      try {
        this.loading = true;
        const response = await fetchDistrictListForUserForm(this.token);
        this.districtList = response.data.data || [];
        console.log("Fetched district list:", this.districtList);
      } catch (error) {
        console.error("Error fetching district list:", error);
      } finally {
        this.loading = false;
      }
    },

    // Load pricing category list
    async loadPricingCategoryList() {
      try {
        this.loading = true;
        const response = await fetchDealerCategoryList(this.token);
        this.pricingCategoryList = response.data.data || [];
        console.log("Fetched pricing categories:", this.pricingCategoryList);
      } catch (error) {
        console.error("Error fetching pricing category list:", error);
      } finally {
        this.loading = false;
      }
    },

    // Load Business Types List
    async loadBusinessTypes() {
      try {
        const response = await fetchBusinessTypesList(this.token);
        this.businessTypesList = response.data.data || [];
        console.log("Fetched types of business:", this.businessTypesList);
        console.log(response);
      } catch (error) {
        console.error("Error fetching types of business:", error);
      }
    },

    // Load Nature of Business List
    async loadBusinessNatures() {
      try {
        const payload = {
          dealer_type_id: this.selectedBusinessType,
        };

        const response = await fetchNatureOfBusinessList(payload, this.token);
        this.natureOfBusinessList = response.data.data || [];
        console.log(
          "Fetched nature of business list:",
          this.natureOfBusinessList,
        );

        return this.natureOfBusinessList; // Return the list for chaining
      } catch (error) {
        console.error("Error fetching nature of business list:", error);
        this.natureOfBusinessList = [];
        throw error; // Re-throw the error to handle in calling function
      }
    },

    // Load Route List
    async loadRoutes() {
      let idToUse = this.profileId;
      if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
        idToUse = this.reportingProfileId;
      }

      try {
        const response = await fetchRouteList(idToUse, this.token);
        this.routesList = response.data.data || [];
        console.log("Fetched routes list:", this.routesList);
      } catch (error) {
        console.error("Error fetching routes list:", error);
      }
    },

    // Load distributors list
    async loadDistributors() {
      try {
        const response = await fetchDistributorsList(
          this.profileId,
          this.token,
        );
        this.distributorList = response.data.data || [];
        await this.loadDistributorId();
        console.log("Fetched distributors:", this.distributorList);
      } catch (error) {
        console.error("Error fetching distributors:", error);
      }
    },

    // Load items list
    async loadItemsList(force = false) {
      if (this.stockLoading) {
        return false;
      }

      if (this.stockItemsLoaded && !force) {
        return true;
      }

      try {
        this.stockLoading = true;

        const companyIdToUse = this.selectedCompany || this.companyId;

        const response = await fetchItemNameList(companyIdToUse, 1, this.token);
        this.itemsList = response.data.data || [];

        this.tryBuildStockItems();

        this.stockItemsLoaded = true;
        return true;
      } catch (error) {
        console.error("Error fetching items List:", error);

        this.itemsList = [];
        this.stockItems = [];
        this.stockItemsLoaded = false;

        this.toast.error("Failed to load stock items.");
        return false;
      } finally {
        this.stockLoading = false;
      }
    },

    ensureStockItemsLoaded() {
      return this.loadItemsList(false);
    },

    processStockItems() {
      if (!Array.isArray(this.itemsList) || this.itemsList.length === 0) {
        this.stockItems = [];
        return;
      }

      this.stockItems = this.itemsList.map((item) => {
        return {
          id: item.id,
          itemName:
            item.item_name || item.itemName || item.name || "Unknown Item",
          itemImage: item.image || item.itemImage || null,
          minQty: "",
          maxQty: "",
        };
      });
    },

    tryBuildStockItems() {
      if (!Array.isArray(this.itemsList) || this.itemsList.length === 0) {
        this.stockItems = [];
        return;
      }

      if (
        this.dealerDetails &&
        Array.isArray(this.dealerDetails.dealer_stock)
      ) {
        this.processDealerStock(this.dealerDetails.dealer_stock);
        return;
      }

      this.processStockItems();
    },

    // Map items with dealer stock
    processDealerStock(dealerStock) {
      if (!Array.isArray(this.itemsList) || this.itemsList.length === 0) {
        this.stockItems = [];
        return;
      }

      const dealerStockList = Array.isArray(dealerStock) ? dealerStock : [];
      const dealerStockMap = {};

      dealerStockList.forEach((stock) => {
        const itemId = stock.itemId || stock.item_id || stock.id || stock.item;

        if (!itemId) {
          return;
        }

        dealerStockMap[String(itemId)] = {
          minQty:
            stock.minQty !== undefined && stock.minQty !== null
              ? stock.minQty
              : stock.min_qty !== undefined && stock.min_qty !== null
              ? stock.min_qty
              : "",

          maxQty:
            stock.maxQty !== undefined && stock.maxQty !== null
              ? stock.maxQty
              : stock.qty !== undefined && stock.qty !== null
              ? stock.qty
              : stock.max_qty !== undefined && stock.max_qty !== null
              ? stock.max_qty
              : "",
        };
      });

      this.stockItems = this.itemsList.map((item) => {
        const stockData = dealerStockMap[String(item.id)];

        return {
          id: item.id,
          itemName:
            item.item_name || item.itemName || item.name || "Unknown Item",
          itemImage: item.image || item.itemImage || null,
          minQty: stockData ? stockData.minQty : "",
          maxQty: stockData ? stockData.maxQty : "",
        };
      });

      console.log("Processed dealer stock items:", this.stockItems);
    },

    // Step Navigation Methods
    async goToNextStep() {
      if (!this.validateStepWithToast(this.activeStep)) {
        return;
      }

      if (this.activeStep === "finance") {
        const stockLoaded = await this.ensureStockItemsLoaded();

        if (!stockLoaded) {
          return;
        }
      }

      const currentIndex = this.steps.findIndex(
        (step) => step.id === this.activeStep,
      );

      if (currentIndex < this.steps.length - 1) {
        this.activeStep = this.steps[currentIndex + 1].id;
      }
    },

    goToPreviousStep() {
      const currentIndex = this.steps.findIndex(
        (step) => step.id === this.activeStep,
      );
      if (currentIndex > 0) {
        this.activeStep = this.steps[currentIndex - 1].id;
      }
    },

    isStepCompleted(stepId) {
      const stepIndex = this.steps.findIndex((step) => step.id === stepId);
      const currentIndex = this.steps.findIndex(
        (step) => step.id === this.activeStep,
      );
      return stepIndex < currentIndex;
    },

    isValidVatNumber(vatNo) {
      // Format: 9 digits - 4 digits (e.g., 123456789-1234)
      const vatRegex = /^\d{9}-\d{4}$/;
      return vatRegex.test(vatNo);
    },

    // Step Validation Methods
    validateStepWithToast(stepId) {
      switch (stepId) {
        case "owner":
          return this.validateOwnerStepWithToast();
        case "business":
          return this.validateBusinessStepWithToast();
        case "finance":
          return this.validateFinanceStepWithToast();
        case "stock":
          return this.validateStockStepWithToast();
        default:
          return false;
      }
    },

    // Validate form before submission and going to next step
    validateOwnerStepWithToast() {
      if (!this.title) {
        this.toast.error("Title is required.");
        return false;
      }
      if (!this.firstName) {
        this.toast.error("First Name is required.");
        return false;
      }
      if (!this.lastName) {
        this.toast.error("Last Name is required.");
        return false;
      }
      if (this.email && !this.isValidEmail(this.email)) {
        this.toast.error("Please enter a valid email address.");
        return false;
      }
      if (!this.mobile) {
        this.toast.error("Mobile Number is required.");
        return false;
      }
      if (!this.isValidMobile(this.mobile)) {
        this.toast.error("Please enter a valid mobile number.");
        return false;
      }
      if (!this.dob) {
        this.toast.error("Date of Birth is required.");
        return false;
      }
      if (!this.identityType) {
        this.toast.error("Identity Type is required.");
        return false;
      }
      if (!this.identityNumber) {
        this.toast.error("Identity Number is required.");
        return false;
      }
      if (!this.isValidIdNumber(this.identityType, this.identityNumber)) {
        this.toast.error(`Please enter a valid ${this.identityType} number.`);
        return false;
      }
      if (this.isIdFrontChanged && !this.idFrontImage) {
        this.toast.error("Front side of Identity document is required.");
        return false;
      }
      if (
        this.isIdBackChanged &&
        this.identityType === "NIC" &&
        !this.idBackImage
      ) {
        this.toast.error("Back side of Identity document is required for NIC.");
        return false;
      }
      return true;
    },
    validateBusinessStepWithToast() {
      if (!this.businessName) {
        this.toast.error("Business Name is required.");
        return false;
      }
      if (!this.selectedCompany) {
        this.toast.error("Company is required.");
        return false;
      }
      if (!this.selectedPricingCategory) {
        this.toast.error("Pricing category is required.");
        return false;
      }
      if (!this.selectedBusinessType) {
        this.toast.error("Business type is required.");
        return false;
      }
      if (!this.selectedNatureOfBusiness) {
        this.toast.error("Nature of Business is required.");
        return false;
      }
      if (!this.selectedRoute) {
        this.toast.error("Route is required.");
        return false;
      }
      if (!this.businessRegNumber) {
        this.toast.error("Business Registration Number is required.");
        return false;
      }
      if (!this.address) {
        this.toast.error("Address is required.");
        return false;
      }
      if (!this.city) {
        this.toast.error("City is required.");
        return false;
      }
      if (!this.selectedDistrict) {
        this.toast.error("District is required.");
        return false;
      }
      if (!this.postalCode) {
        this.toast.error("Postal Code is required.");
        return false;
      }
      if (!this.selectedDistributor) {
        this.toast.error("Distributor is required.");
        return false;
      }
      if (!this.selectedSalesExecutive) {
        this.toast.error("Sales Executive is required.");
        return false;
      }
      if (!this.latitude) {
        this.toast.error("Latitude is required.");
        return false;
      }
      if (!this.longitude) {
        this.toast.error("Longitude is required.");
        return false;
      }
      if (this.isBrDocumentChanged && !this.brDocument) {
        this.toast.error("Business Registration Document is required.");
        return false;
      }
      if (this.isAgreementDocumentChanged && !this.agreementDocument) {
        this.toast.error("Agreement Document is required.");
        return false;
      }
      return true;
    },
    validateFinanceStepWithToast() {
      // VAT Number Validation
      if (this.isCheckedVat === 1) {
        if (!this.vatNo || this.vatNo.trim() === "") {
          this.toast.error("VAT Number is required when VAT is enabled.");
          return false;
        }
        if (!this.isValidVatNumber(this.vatNo)) {
          this.toast.error(
            "VAT Number must be in the format: 123456789-1234 (9 digits, hyphen, 4 digits).",
          );
          return false;
        }
      }

      // SVAT Number Validation (if you want similar validation)
      if (this.isCheckedSvat === 1) {
        if (!this.svatNo || this.svatNo.trim() === "") {
          this.toast.error("SVAT Number is required when SVAT is enabled.");
          return false;
        }
        if (!this.isValidVatNumber(this.svatNo)) {
          this.toast.error(
            "SVAT Number must be in the format: 123456789-1234 (9 digits, hyphen, 4 digits).",
          );
          return false;
        }
      }

      if (this.vatContactNo && !this.isValidMobile(this.vatContactNo)) {
        this.toast.error(
          "VAT Contact No must be a valid 10 digit mobile number.",
        );
        return false;
      }

      if (!this.creditLimitDays) {
        this.toast.error("Credit limit days is required.");
        return false;
      }
      if (this.creditLimitDays && isNaN(this.creditLimitDays)) {
        this.toast.error("Credit Limit Days must be a numeric value.");
        return false;
      }
      // Credit Limit Required (0 allowed)
      if (
        this.creditLimit === "" ||
        this.creditLimit === null ||
        this.creditLimit === undefined
      ) {
        this.toast.error("Credit limit is required.");
        return false;
      }
      // Credit Limit numeric
      if (isNaN(Number(this.creditLimit))) {
        this.toast.error("Credit Limit must be a numeric value.");
        return false;
      }
      return true;
    },
    validateStockStepWithToast() {
      const hasStockData = this.stockItems.some(
        (item) => item.minQty || item.maxQty,
      );
      if (!hasStockData) {
        this.toast.error("Add at least one item minimum and maximum quantity.");
        return false;
      }
      return true;
    },

    // New method for toggling visit days
    toggleVisitDay(day) {
      this.dealerVisitFrequency[day] =
        this.dealerVisitFrequency[day] === "1" ? "0" : "1";
    },
    capitalizeFirst(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    // Document Actions Methods
    triggerFileInput(inputId) {
      this.$refs[inputId].click();
    },

    // Modified document click handler
    handleDocumentClick(documentUrl, title, documentType, isChanged = false) {
      // If document is changed (base64 from file upload), open in modal
      if (isChanged) {
        this.openDocumentModal(documentUrl, title, documentType);
      } else {
        // If not changed (URL from API), open original document in new tab
        window.open(documentUrl, "_blank");
      }
    },

    // Modified method to open modal for both images and PDFs
    openDocumentModal(documentUrl, title = "", documentType = null) {
      this.selectedImage = documentUrl;
      this.selectedImageTitle = title;
      this.selectedDocumentType = documentType;
      this.showImageModal = true;
    },

    // Close Image Modal
    closeImageModal() {
      this.showImageModal = false;
      this.selectedImage = "";
      this.selectedImageTitle = "";
      this.selectedDocumentType = null;
    },

    // File Upload Handlers with Simulated Progress - Updated to accept PDF and images
    async handleIdFrontImage(event) {
      const file = event.target.files[0];
      if (file) {
        if (!this.validateFile(file, "mixed")) return;

        // Validation logic...
        if (!file && !this.originalIdFrontImage) {
          this.toast.error("Front side of Identity document is required.");
          return;
        }

        this.idFrontImage = file;
        this.idFrontImageType = file.type;
        this.isIdFrontChanged = true;
        this.uploading.idFront = true;
        this.uploadProgress.idFront = 0;

        await this.simulateUploadProgress("idFront");

        // --- FIX START: Read BOTH Images and PDFs as Data URL ---
        const reader = new FileReader();
        reader.onload = (e) => {
          // This stores the actual Base64 data (data:application/pdf;base64,...)
          this.idFrontImagePreview = e.target.result;
          this.uploading.idFront = false;
        };
        reader.readAsDataURL(file);
        // --- FIX END ---
      }
    },
    async handleIdBackImage(event) {
      const file = event.target.files[0];
      if (file) {
        if (!this.validateFile(file, "mixed")) return;

        if (this.identityType === "NIC" && !file && !this.originalIdBackImage) {
          this.toast.error(
            "Back side of Identity document is required for NIC.",
          );
          return;
        }

        this.idBackImage = file;
        this.idBackImageType = file.type;
        this.isIdBackChanged = true;
        this.uploading.idBack = true;
        this.uploadProgress.idBack = 0;

        await this.simulateUploadProgress("idBack");

        // --- FIX START ---
        const reader = new FileReader();
        reader.onload = (e) => {
          this.idBackImagePreview = e.target.result;
          this.uploading.idBack = false;
        };
        reader.readAsDataURL(file);
        // --- FIX END ---
      }
    },
    async handleBrDocument(event) {
      const file = event.target.files[0];
      if (file) {
        if (!this.validateFile(file, "mixed")) return;

        if (!file && !this.originalBrDocument) {
          this.toast.error("Business Registration Document is required.");
          return;
        }

        this.brDocument = file;
        this.brDocumentName = file.name;
        this.brDocumentType = file.type;
        this.isBrDocumentChanged = true;
        this.uploading.brDocument = true;
        this.uploadProgress.brDocument = 0;

        await this.simulateUploadProgress("brDocument");

        // --- FIX START ---
        const reader = new FileReader();
        reader.onload = (e) => {
          this.brDocumentPreview = e.target.result; // Stores actual PDF data now
          this.uploading.brDocument = false;
        };
        reader.readAsDataURL(file);
        // --- FIX END ---
      }
    },
    async handleAgreementDocument(event) {
      const file = event.target.files[0];
      if (file) {
        if (!this.validateFile(file, "mixed")) return;

        if (!file && !this.originalAgreementDocument) {
          this.toast.error("Agreement Document is required.");
          return;
        }

        this.agreementDocument = file;
        this.agreementDocumentName = file.name;
        this.agreementDocumentType = file.type;
        this.isAgreementDocumentChanged = true;
        this.uploading.agreementDocument = true;
        this.uploadProgress.agreementDocument = 0;

        await this.simulateUploadProgress("agreementDocument");

        // --- FIX START ---
        const reader = new FileReader();
        reader.onload = (e) => {
          this.agreementDocumentPreview = e.target.result;
          this.uploading.agreementDocument = false;
        };
        reader.readAsDataURL(file);
        // --- FIX END ---
      }
    },

    // Simulate upload progress
    simulateUploadProgress(type) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          this.uploadProgress[type] += 10;
          if (this.uploadProgress[type] >= 100) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
      });
    },

    // Add this method to your methods section
    validateDocumentRemoval(type) {
      switch (type) {
        case "idFront":
          if (this.isIdFrontChanged && !this.idFrontImage) {
            return "Front side of Identity document cannot be removed.";
          }
          break;
        case "idBack":
          if (
            this.identityType === "NIC" &&
            this.isIdBackChanged &&
            !this.idBackImage
          ) {
            return "Back side of Identity document cannot be removed for NIC.";
          }
          break;
        case "brDocument":
          if (this.isBrDocumentChanged && !this.brDocument) {
            return "Business Registration Document cannot be removed.";
          }
          break;
        case "agreementDocument":
          if (this.isAgreementDocumentChanged && !this.agreementDocument) {
            return "Agreement Document cannot be removed.";
          }
          break;
      }
      return null;
    },

    // Remove document
    removeDocument(type) {
      // Validate if this document can be removed
      const validationError = this.validateDocumentRemoval(type);
      if (validationError) {
        this.toast.error(validationError);
        return;
      }

      switch (type) {
        case "idFront":
          this.idFrontImage = null;
          this.idFrontImagePreview = null;
          this.idFrontImageType = null;
          this.isIdFrontChanged = true;
          this.$refs.idFrontImage.value = "";
          break;
        case "idBack":
          this.idBackImage = null;
          this.idBackImagePreview = null;
          this.idBackImageType = null;
          this.isIdBackChanged = true;
          this.$refs.idBackImage.value = "";
          break;
        case "brDocument":
          this.brDocument = null;
          this.brDocumentPreview = null;
          this.brDocumentName = "";
          this.brDocumentType = null;
          this.isBrDocumentChanged = true;
          this.$refs.brDocument.value = "";
          break;
        case "agreementDocument":
          this.agreementDocument = null;
          this.agreementDocumentPreview = null;
          this.agreementDocumentName = "";
          this.agreementDocumentType = null;
          this.isAgreementDocumentChanged = true;
          this.$refs.agreementDocument.value = "";
          break;
      }
    },

    // Image handling methods
    isValidBase64Image(base64String) {
      if (!base64String) return false;
      const base64Regex =
        /^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/;
      if (base64Regex.test(base64String)) {
        return true;
      }
      const pureBase64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
      if (pureBase64Regex.test(base64String)) {
        return base64String.length > 0;
      }
      return false;
    },
    getBase64ImageSrc(base64String) {
      if (!base64String) return "";
      if (base64String.startsWith("data:image/")) {
        return base64String;
      }
      return `data:image/jpeg;base64,${base64String}`;
    },
    handleStockImageError(index) {
      this.$set(this.stockImageErrors, index, true);
    },

    // Validate email
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    // Validate mobile number
    isValidMobile(mobile) {
      const mobileRegex = /^\d{10}$/; // only 10 digits allowed
      return mobileRegex.test(mobile);
    },

    // Validate ID number
    isValidIdNumber(idType, idNumber) {
      if (!idType || !idNumber) return false;

      idNumber = idNumber.toUpperCase();

      switch (idType) {
        case "NIC":
          // Old NIC: 9 digits + V/X (e.g., 911234567V)
          // New NIC: 12 digits (e.g., 200012345678)
          return /^(?:\d{9}[VX]|\d{12})$/.test(idNumber);

        case "Passport":
          // Sri Lankan Passport: 1 letter + 7 digits (e.g., N1234567)
          return /^[A-Z]\d{7}$/.test(idNumber);

        case "Driving License":
          // Sri Lankan Driving License: 10 digits (e.g., 2000096271)
          return /^\d{10}$/.test(idNumber);

        default:
          return false;
      }
    },

    // File validation - Updated to accept both images and PDFs
    validateFile(file, type) {
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (file.size > maxSize) {
        this.toast.error("File size must be less than 5MB");
        return false;
      }

      if (type === "mixed") {
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "application/pdf",
        ];
        if (!allowedTypes.includes(file.type)) {
          this.toast.error("Please select a PDF, JPG, PNG, or JPEG file");
          return false;
        }
      }

      if (type === "image" && !file.type.startsWith("image/")) {
        this.toast.error("Please select a valid image file");
        return false;
      }

      if (
        type === "document" &&
        !file.type.startsWith("image/") &&
        file.type !== "application/pdf"
      ) {
        this.toast.error("Please select a PDF or image file");
        return false;
      }

      return true;
    },

    async submitDealerUpdate() {
      console.log("Update button clicked");

      if (this.stockLoading) {
        this.toast.error("Please wait until stock items are loaded.");
        return;
      }

      await this.updateDealer();
    },

    // Update dealer information
    async updateDealer() {
      try {
        this.updating = true;

        const toBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (error) => reject(error);
          });

        // Get created by Id based on user role
        let idToUse = this.profileId;
        if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
          idToUse = this.reportingProfileId;
        }

        // Prepare payload with conditional document inclusion
        const payload = {
          // Owner Information
          title: this.title,
          first_name: this.firstName,
          last_name: this.lastName,
          mobile: this.mobile,
          username: this.mobile,
          dob: this.dob,
          email: this.email,
          id_type: this.identityType,
          id_number: this.identityNumber,

          // Business Information
          business_name: this.businessName,
          company_id: Number(this.selectedCompany),
          dealer_cat_id: Number(this.selectedPricingCategory),
          dealer_type_id: Number(this.selectedBusinessType),
          nature_of_businesses_id: Number(this.selectedNatureOfBusiness),
          route_id: Number(this.selectedRoute),
          br_number: this.businessRegNumber,
          address1: this.address,
          city: this.city,
          district_id: Number(this.selectedDistrict),
          post_code: this.postalCode,
          distributor_id: this.selectedDistributor,
          dealer_group_id: this.selectedDealerGroup && Number(this.selectedDealerGroup) !== 0 ? Number(this.selectedDealerGroup) : null,
          sales_executive_id: this.selectedSalesExecutive,
          latitude: this.latitude || "0.000000",
          longitude: this.longitude || "0.000000",
          sap_code: this.sapCode,
          reg_type: 15,
          created_by: Number(idToUse),
          is_home_delivery: this.is_home_delivery,
          is_eligible_trust: this.is_eligible_trust,
          is_active: this.isActive,

          // Financial Information
          vat_liable: this.isCheckedVat == 1 ? 1 : 0,
          svat_liable: this.isCheckedSvat == 1 ? 1 : 0,
          vat_no: this.vatNo || null,
          svat_no: this.svatNo || null,
          parent_address: this.vatAddress || null,
          parent_name: this.vatName || null,
          parent_contact_no: this.vatContactNo || null,
          credit_limit: this.creditLimit,
          credit_limit_days: Number(this.creditLimitDays),
          eligible_printed_invoice: this.eligiblePrintedInvoice,

          // Dealer Visit Frequency
          monday: this.dealerVisitFrequency.monday === "1" ? 1 : 0,
          tuesday: this.dealerVisitFrequency.tuesday === "1" ? 1 : 0,
          wednesday: this.dealerVisitFrequency.wednesday === "1" ? 1 : 0,
          thursday: this.dealerVisitFrequency.thursday === "1" ? 1 : 0,
          friday: this.dealerVisitFrequency.friday === "1" ? 1 : 0,
          saturday: this.dealerVisitFrequency.saturday === "1" ? 1 : 0,
          sunday: this.dealerVisitFrequency.sunday === "1" ? 1 : 0,

          // Stock Management
          stock_items: this.stockItems
            .map((item) => ({
              id: Number(item.id),
              qty: Number(item.maxQty) || 0,
              min_qty: Number(item.minQty) || 0,
            }))
            .filter((item) => item.qty > 0 || item.min_qty > 0),
        };

        // Conditionally include ID Front Image
        if (this.isIdFrontChanged && this.idFrontImage) {
          const idFrontBase64 = await toBase64(this.idFrontImage);
          payload.user_front_img = idFrontBase64;
          payload.user_front_img_name = this.idFrontImage.name || "front.jpg";
        }

        // Conditionally include ID Back Image
        if (this.isIdBackChanged && this.idBackImage) {
          const idBackBase64 = await toBase64(this.idBackImage);
          payload.user_back_img = idBackBase64;
          payload.user_back_img_name = this.idBackImage.name || "back.jpg";
        }

        // Conditionally include Business Registration Document
        if (this.isBrDocumentChanged && this.brDocument) {
          const brDocBase64 = await toBase64(this.brDocument);
          payload.br_scan_img = brDocBase64;
        }

        // Conditionally include Agreement Document
        if (this.isAgreementDocumentChanged && this.agreementDocument) {
          const agreementBase64 = await toBase64(this.agreementDocument);
          payload.agreement_img = agreementBase64;
          payload.agreement_number = this.businessRegNumber;
        }

        // Handle document removal (when document is set to null)
        if (this.isIdFrontChanged && !this.idFrontImage) {
          payload.user_front_img = "";
          payload.user_front_img_name = "";
        }

        if (this.isIdBackChanged && !this.idBackImage) {
          payload.user_back_img = "";
          payload.user_back_img_name = "";
        }

        if (this.isBrDocumentChanged && !this.brDocument) {
          payload.br_scan_img = "";
        }

        if (this.isAgreementDocumentChanged && !this.agreementDocument) {
          payload.agreement_img = "";
          payload.agreement_number = "";
        }

        console.log("Update Dealer Payload:");
        console.log(JSON.stringify(payload, null, 2));

        // Call dealer edit API for edit existing dealer details
        const response = await updateDealer(this.id, this.token, payload);

        if (response.data.status === "success") {
          this.toast.success(response.data.message);
          this.$router.push("/dealers");
        } else {
          // Combine all errors into ONE message
          let errorMessage = response.data.message || "Validation Error!";

          if (
            response.data.data &&
            Object.keys(response.data.data).length > 0
          ) {
            errorMessage += "\n\n";
            Object.keys(response.data.data).forEach((field) => {
              const errors = response.data.data[field];
              errors.forEach((error) => {
                errorMessage += "• " + error + "\n";
              });
            });
          }

          this.toast.error(errorMessage);
        }
      } catch (error) {
        console.error("Edit dealer error:", error);

        // Handle validation errors in catch block (when API throws error)
        if (error.response && error.response.data) {
          let errorMessage = error.response.data.message || "Validation Error!";

          if (
            error.response.data.data &&
            Object.keys(error.response.data.data).length > 0
          ) {
            errorMessage += "\n\n";
            Object.keys(error.response.data.data).forEach((field) => {
              const errors = error.response.data.data[field];
              errors.forEach((err) => {
                errorMessage += "• " + err + "\n";
              });
            });
          }

          this.toast.error(errorMessage);
        } else {
          this.toast.error("Failed to update dealer. Please try again.");
        }
      } finally {
        this.updating = false;
      }
    },

    // Cancel submission
    cancel() {
      this.$router.push("/dealers");
    },
  },
};
</script>

<style scoped>
/* Tracker Styles */
.tracker-container {
  position: relative;
  margin-bottom: 2rem;
  width: 100%;
}

.tracker-steps {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.tracker-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}

.step-icon-container {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border: 3px solid #e5e7eb;
  transition: all 0.3s ease;
  z-index: 2;
}

.tracker-step.active .step-icon-container {
  background-color: #1c66ad;
  border-color: #1c66ad;
  color: white;
  transform: scale(1.1);
}

.tracker-step.completed .step-icon-container {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.step-icon-container i {
  font-size: 1.2rem;
}

.step-label {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  color: #6b7280;
  transition: color 0.3s ease;
}

.tracker-step.active .step-label {
  color: #1c66ad;
  font-weight: 600;
}

.tracker-step.completed .step-label {
  color: #28a745;
}

.tracker-line {
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #e5e7eb;
  z-index: 0;
}

/* Step Navigation Styles */
.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  width: 100%;
}

.step-navigation-left {
  display: flex;
  justify-content: flex-start;
  flex: 1;
}

.step-navigation-right {
  display: flex;
  justify-content: flex-end;
  flex: 1;
}

/* For first step where only Next button exists */
.step-navigation:has(.step-navigation-left:empty) .step-navigation-right {
  justify-content: flex-end;
  width: 100%;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
  min-width: 100px;
}

.btn-primary {
  background-color: #1c66ad;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #155a9c;
  transform: scale(1.05);
}

.btn-primary:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background-color: #4b5563;
  transform: scale(1.05);
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
  transform: scale(1.05);
}

.btn-success:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

/* Financial Information Styles - Full width */
.full-width-section {
  width: 100%;
  max-width: 100%;
}

.finance-container {
  width: 100%;
  max-width: 100%;
}

.finance-row {
  display: flex;
  gap: 15px;
  width: 100%;
  margin-bottom: 15px;
}

.finance-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Prevent flex items from overflowing */
}

.vat-section,
.svat-section {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
  height: fit-content;
  width: 100%;
}

.vat-checkbox-container,
.svat-checkbox-container,
.homeDelivery-checkbox-container {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.vat-label,
.svat-label,
.homeDelivery-label {
  margin-left: 10px;
  font-weight: bold;
  color: #333;
  font-size: 14px;
}

.vat-input-container,
.svat-input-container {
  width: 100%;
}

.vat-input,
.svat-input {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  box-sizing: border-box;
}

.vat-input:focus,
.svat-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

/* Eligible Printed Invoice Styles */
.eligible-printed-invoice-container,
.is-active-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.eligible-invoice-label,
.is-active-label {
  font-weight: bold;
  color: #333;
  font-size: 16px;
}

/* Toggle Switch Styles */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #28a745;
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

/* Dealer Visit Frequency Styles */
.frequency-selection {
  margin-top: 1rem;
}

.frequency-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
}

.visit-frequency-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.day-box {
  flex: 1 1 12%;
  min-width: 80px;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px 10px;
  border-radius: 8px;
  background-color: #f3f4f6;
  color: #374151;
  font-weight: 500;
  text-align: center;
  border: 2px solid #e5e7eb;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
}

.day-box:hover {
  border-color: #1c66ad;
  background-color: #e0f2fe;
}

.day-box.active {
  border: 2px solid #1c66ad;
  background-color: #e0f2fe;
  box-shadow: 0 4px 8px rgba(28, 102, 173, 0.2);
}

.day-box.inactive {
  border: 2px solid #e5e7eb;
  background-color: #f3f4f6;
}

.day-box-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  width: 100%;
}

.day-box i.fa-calendar-day {
  font-size: 1.2rem;
}

.check-icon {
  position: absolute;
  top: -20px;
  right: -5px;
  background-color: #1c66ad;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Existing styles */
.req {
  color: red;
  margin-left: 5px;
}

.checkbox {
  width: 15px;
  height: 15px;
  border: 2px solid #007bff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: #007bff;
  background-color: white;
  border-radius: 4px;
  transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
  position: relative;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
}

.section {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #ccc;
  color: #333;
}

.scrollable-dropdown {
  max-height: 50px;
  overflow-y: auto;
}

/* Add styles to visually indicate disabled state */
.scrollable-dropdown:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
}

/* You can also add a tooltip or indicator for auto-filled fields */
.dropdown-item label::after {
  content: attr(data-auto-filled);
  font-size: 0.75rem;
  color: #28a745;
  margin-left: 5px;
  font-weight: normal;
}

.d-flex {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
}

/* Single column layout for form fields */
.dropdown-container.single-column {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}

.dropdown-item {
  width: 100%;
}

.full-width {
  max-width: 100%;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select,
input,
textarea {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.file-input-hidden {
  display: none;
}

select:focus,
input:focus,
textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.remark-textarea {
  width: 100%;
  padding: 12px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
}

.image-preview {
  margin-top: 10px;
  max-width: 200px;
}

.image-preview img {
  max-width: 100%;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.file-name {
  margin-top: 5px;
  font-style: italic;
  color: #666;
}

.image-preview {
  margin-top: 10px;
  max-width: 200px;
}

.image-preview img {
  max-width: 100%;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.file-name {
  margin-top: 5px;
  font-style: italic;
  color: #666;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
}

.popup-columns {
  display: flex;
  gap: 20px;
  height: 100%;
  width: 100%;
}

.popup-column {
  display: flex;
  flex-direction: column;
}

.details-column {
  width: 60%;
  padding-right: 20px;
  border-right: 1px solid #e5e7eb;
}

.documents-column {
  width: 40%;
  padding-left: 10px;
}

.info-section {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
  width: 100%;
}

.documents-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
}

.document-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

/* FIXED: Use same method as owner ID section */
.document-image-container {
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.document-image-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.document-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.document-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  color: #6b7280;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
}

.document-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #9ca3af;
}

.document-placeholder span {
  font-size: 0.875rem;
  font-weight: 500;
}

/* FIXED: PDF preview styles - same as owner section */
.pdf-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fef3f2;
  color: #dc2626;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
}

.pdf-preview i {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.pdf-preview span {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
}

.pdf-preview small {
  display: block;
  margin-top: 5px;
  font-size: 0.8rem;
  color: #6b7280;
}

/* FIXED: Invalid document preview styles - same as owner section */
.invalid-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fef3f2;
  color: #dc2626;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
}

.invalid-preview i {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.invalid-preview span {
  font-size: 0.875rem;
  font-weight: 500;
}

.invalid-preview small {
  display: block;
  margin-top: 5px;
  font-size: 0.75rem;
  color: #dc2626;
}

.document-image-container:hover .document-placeholder {
  background-color: #e5e7eb;
  border-color: #9ca3af;
}

/* Document Actions Overlay */
.document-actions-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.action-btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.preview-btn {
  background: linear-gradient(45deg, #2196f3, #21cbf3);
}

.preview-btn:hover {
  background: linear-gradient(45deg, #1976d2, #03a9f4);
  transform: scale(1.15);
  box-shadow: 0 6px 12px rgba(33, 150, 243, 0.4);
}

.change-btn {
  background: linear-gradient(45deg, #4caf50, #66bb6a);
}

.change-btn:hover {
  background: linear-gradient(45deg, #388e3c, #4caf50);
  transform: scale(1.15);
  box-shadow: 0 6px 12px rgba(76, 175, 80, 0.4);
}

.remove-btn {
  background: linear-gradient(45deg, #f44336, #e53935);
}

.remove-btn:hover {
  background: linear-gradient(45deg, #d32f2f, #c62828);
  transform: scale(1.15);
  box-shadow: 0 6px 12px rgba(244, 67, 54, 0.4);
}

.action-buttons {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
}

.action-labels {
  display: flex;
  gap: 25px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.action-labels span {
  text-align: center;
  min-width: 50px;
}

.document-label {
  font-weight: 500;
  color: #374151;
  text-align: center;
  font-size: 14px;
  margin-top: 8px;
}

/* Stock Management Styles */
.stock-table-container {
  overflow-x: auto;
}

.table-container {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-container th,
.table-container td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.table-container th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.reorder-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.min-max-labels {
  display: flex;
  justify-content: space-between;
  font-weight: normal;
  font-size: 0.875rem;
  color: #6b7280;
}

.item-image-cell {
  width: 100px;
}

.item-image-container {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
}

.item-placeholder i {
  color: #9ca3af;
  font-size: 1rem;
}

.item-name-cell {
  font-weight: 500;
}

.reorder-cell {
  width: 200px;
}

.reorder-levels {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.min-level,
.max-level {
  flex: 1;
  text-align: center;
  padding: 0.5rem;
  background-color: #f3f4f6;
  border-radius: 4px;
  font-weight: 500;
}

.qty-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}

.qty-input:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

/* Upload progress styles */
.document-placeholder small {
  display: block;
  margin-top: 5px;
  font-size: 0.75rem;
  color: #9ca3af;
}

.upload-progress {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(45deg, #4caf50, #66bb6a);
  transition: width 0.3s ease;
}

.upload-progress span {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  min-width: 30px;
}

.m-4 {
  margin: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .popup-columns {
    flex-direction: column;
  }

  .details-column,
  .documents-column {
    width: 100%;
    padding: 0;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
  }

  .step-icon-container {
    width: 50px;
    height: 50px;
  }

  .step-icon-container i {
    font-size: 1rem;
  }

  .step-label {
    font-size: 0.75rem;
  }

  .tracker-line {
    top: 25px;
  }

  .dropdown-container.single-column {
    grid-template-columns: 1fr;
  }

  .vat-section,
  .svat-section {
    margin: 5px 0;
  }

  .action-buttons {
    gap: 10px;
  }

  .action-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .action-labels {
    gap: 15px;
    font-size: 11px;
  }

  .action-labels span {
    min-width: 40px;
  }

  .visit-frequency-grid {
    gap: 8px;
  }

  .day-box {
    flex: 1 1 45%;
    min-width: 100px;
    min-height: 80px;
  }

  .finance-row {
    flex-direction: column;
    gap: 10px;
  }

  .step-navigation {
    flex-direction: column;
    gap: 10px;
  }

  .step-navigation-left,
  .step-navigation-right {
    justify-content: center;
    width: 100%;
  }

  .step-navigation .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .documents-grid {
    grid-template-columns: 1fr;
  }

  .document-image-container {
    height: 180px;
  }

  .action-buttons {
    gap: 8px;
  }

  .action-btn {
    width: 35px;
    height: 35px;
    font-size: 14px;
  }

  .action-labels {
    gap: 12px;
    font-size: 10px;
  }

  .action-labels span {
    min-width: 35px;
  }

  .day-box {
    flex: 1 1 100%;
  }

  .tracker-step {
    flex: 1;
  }

  .step-icon-container {
    width: 40px;
    height: 40px;
  }

  .step-icon-container i {
    font-size: 0.9rem;
  }

  .step-label {
    font-size: 0.7rem;
  }

  .tracker-line {
    top: 20px;
  }
}

.vat-extra-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.vat-extra-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vat-extra-item label {
  margin-bottom: 5px;
  color: #333;
  font-size: 13px;
  font-weight: 700;
}

.vat-extra-full {
  grid-column: 1 / -1;
}

@media (max-width: 768px) {
  .vat-extra-grid {
    grid-template-columns: 1fr;
  }
}
</style>
